import { useMemo } from 'react';
import { normalizeParametroPrecoBase } from '@/shared';
import { useClient, useSelectedClientScope, useTables } from '@/hooks';
import {
    PercentualFornecedorProps,
    PriceProductProps,
    ProductProps,
    TaxSubstitutionProps,
} from '@/types';
import { useCalcPrice } from '../calcPrices/useCalcPrice';
import { useComboCartItems } from './useComboCartItems';
import {
    applyComboPriceBenefitToProduct,
    ComboPriceBenefit,
    getComboPriceBenefitForCartItem,
} from './comboCartUtils';

interface UseComboCartPriceParams {
    cartItemUid: string;
    product: ProductProps;
    option_uid: string;
    paymentCondition: number;
    applyComboDiscount?: boolean;
    /** Quando false, não calcula preços de combo (fluxo padrão do carrinho). */
    enabled?: boolean;
}

const EMPTY_COMBO_CART_PRICE = {
    hasComboDiscount: false,
    comboBenefit: null,
    percentDiscount: null,
    fixedPrice: null,
    originalPriceRecord: null,
    comboPriceRecord: null,
    originalUnitPrice: null,
    comboUnitPrice: null,
} as const;

const getPaymentConditions = (
    scopedPayments: unknown,
    clientPayments: unknown
): number[] => {
    const fromPayments = (pay: unknown) =>
        (Array.isArray(pay) ? pay : [])
            .map((item: { cod_condicao?: number; COD_CONDICAO?: number }) =>
                Number(item?.cod_condicao ?? item?.COD_CONDICAO)
            )
            .filter((value) => Number.isFinite(value) && value > 0);

    const scoped = fromPayments(scopedPayments);
    if (scoped.length > 0) return scoped;

    return fromPayments(clientPayments);
};

const getUnitPrice = (price?: PriceProductProps | null) =>
    price?.VLR_FINAL ?? price?.VLR_PRECO ?? null;

export const useComboCartPrice = ({
    cartItemUid,
    product,
    option_uid,
    paymentCondition,
    applyComboDiscount = true,
    enabled = true,
}: UseComboCartPriceParams) => {
    const { tables } = useTables();
    const { selectedClient } = useClient();
    const selectedClientScope = useSelectedClientScope();
    const { findPrice, handlePrices } = useCalcPrice();
    const { combos, getComboCartItemInfo, isComboCompleteById } = useComboCartItems();

    const condicoes = useMemo(
        () => getPaymentConditions(selectedClientScope?.COD_PAGAMENTO, selectedClient?.COD_PAGAMENTO),
        [selectedClientScope?.COD_PAGAMENTO, selectedClient?.COD_PAGAMENTO]
    );

    const rawBaseProduct = useMemo(() => {
        const dict = tables.current.dicionario_produtos as Record<number, ProductProps> | undefined;
        const fromDict = dict?.[product.COD_PRODUTO];

        if (fromDict?.PRECO) {
            return {
                ...fromDict,
                EMBALAGEM: fromDict.EMBALAGEM?.length ? fromDict.EMBALAGEM : product.EMBALAGEM,
            };
        }

        return product;
    }, [product, tables]);

    const comboBenefit = useMemo((): ComboPriceBenefit | null => {
        if (!enabled || !applyComboDiscount) return null;

        return getComboPriceBenefitForCartItem(
            getComboCartItemInfo(cartItemUid),
            combos,
            isComboCompleteById,
            { sku: product.COD_PRODUTO, option_uid }
        );
    }, [
        enabled,
        applyComboDiscount,
        cartItemUid,
        combos,
        getComboCartItemInfo,
        isComboCompleteById,
        product.COD_PRODUTO,
        option_uid,
    ]);

    const priceCalcParams = useMemo(() => {
        if (!enabled) {
            return {
                parametroPreco: [],
                percentualFornecedor: [],
                listSubstTrib: [],
                parametroPrecoPromocao: undefined,
            };
        }
        const codLoja = selectedClientScope?.COD_LOJA;
        const tabVenda = selectedClientScope?.COD_TABELA_VENDA;
        const percentual = (tables.current.percentual_fornecedor as PercentualFornecedorProps[]) ?? [];
        const listSubstTrib = (tables.current.cod_regime_tributario as TaxSubstitutionProps[]) ?? [];
        const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
        const percentualFornecedor = (Array.isArray(percentual) ? percentual : []).filter(
            (entry) => `${entry.NROTABVENDA}` === `${tabVenda}`
        );

        const parametroPreco = normalizeParametroPrecoBase(
            tables.current.parametro_preco_base,
            codLoja,
            tabVenda,
            condicoes
        );

        return {
            parametroPreco,
            percentualFornecedor,
            listSubstTrib,
            parametroPrecoPromocao,
        };
    }, [
        selectedClientScope?.COD_LOJA,
        selectedClientScope?.COD_TABELA_VENDA,
        tables,
        condicoes,
        enabled,
    ]);

    // Resolve tabela x promoção antes do combo — senão o combo desconta a tabela crua e a promoção reverte o desconto.
    const baseProduct = useMemo(() => {
        if (!enabled || !condicoes.length || !rawBaseProduct?.COD_PRODUTO) return rawBaseProduct;

        const resolvedPrecos = handlePrices(
            condicoes,
            rawBaseProduct,
            priceCalcParams.parametroPreco,
            priceCalcParams.percentualFornecedor,
            priceCalcParams.listSubstTrib,
            priceCalcParams.parametroPrecoPromocao,
            'useComboCartPrice-resolve-base'
        );

        return resolvedPrecos.length ? { ...rawBaseProduct, PRECO: resolvedPrecos } : rawBaseProduct;
    }, [rawBaseProduct, condicoes, handlePrices, priceCalcParams, enabled]);

    const originalPriceRecord = useMemo((): PriceProductProps | null => {
        if (!enabled || !baseProduct?.COD_PRODUTO || !condicoes.length) return null;

        const precos = handlePrices(
            condicoes,
            baseProduct,
            priceCalcParams.parametroPreco,
            priceCalcParams.percentualFornecedor,
            priceCalcParams.listSubstTrib,
            priceCalcParams.parametroPrecoPromocao,
            'useComboCartPrice-original'
        );

        const found = findPrice(precos, option_uid, paymentCondition);
        return getUnitPrice(found) != null ? found : null;
    }, [
        baseProduct,
        condicoes,
        handlePrices,
        findPrice,
        option_uid,
        paymentCondition,
        priceCalcParams,
        enabled,
    ]);

    const comboPriceRecord = useMemo((): PriceProductProps | null => {
        if (!enabled || !comboBenefit || !baseProduct?.COD_PRODUTO || !condicoes.length) return null;

        const benefitProduct = applyComboPriceBenefitToProduct(baseProduct, comboBenefit, option_uid);
        const calcOptions =
            comboBenefit.type === 'PRECO_FIXO' ? { skipPaymentAccrual: true as const } : undefined;

        const precos = handlePrices(
            condicoes,
            benefitProduct,
            priceCalcParams.parametroPreco,
            priceCalcParams.percentualFornecedor,
            priceCalcParams.listSubstTrib,
            priceCalcParams.parametroPrecoPromocao,
            'useComboCartPrice-combo',
            calcOptions
        );

        const found = findPrice(precos, option_uid, paymentCondition);
        return getUnitPrice(found) != null ? found : null;
    }, [
        comboBenefit,
        baseProduct,
        condicoes,
        handlePrices,
        findPrice,
        option_uid,
        paymentCondition,
        priceCalcParams,
        enabled,
    ]);

    const originalUnitPrice = enabled ? getUnitPrice(originalPriceRecord) : null;
    const comboUnitPrice = useMemo((): number | null => {
        if (!enabled) return null;
        if (comboBenefit?.type === 'PRECO_FIXO') {
            return comboBenefit.fixedPrice;
        }
        return getUnitPrice(comboPriceRecord);
    }, [enabled, comboBenefit, comboPriceRecord]);

    const hasComboDiscount = useMemo(() => {
        if (!enabled || !comboBenefit || comboUnitPrice == null) return false;

        if (originalUnitPrice == null) return true;

        return comboUnitPrice !== originalUnitPrice;
    }, [enabled, comboBenefit, comboUnitPrice, originalUnitPrice]);

    if (!enabled) {
        return EMPTY_COMBO_CART_PRICE;
    }

    return {
        hasComboDiscount,
        comboBenefit,
        percentDiscount: comboBenefit?.type === 'PERCENTUAL' ? comboBenefit.percent : null,
        fixedPrice: comboBenefit?.type === 'PRECO_FIXO' ? comboBenefit.fixedPrice : null,
        originalPriceRecord,
        comboPriceRecord,
        originalUnitPrice,
        comboUnitPrice,
    };
};
